/* The `/settings` read and write path — who the user is, which org, which role,
   which plan, in one query.

   It lives in `shared/api/` rather than `features/settings/api/` because the app
   shell and more than one feature read it, and two features write it: the
   Settings page edits a profile, the first-run wizard fills in the name it
   just asked for (rule book §1 rule 5 — a feature never imports a feature).

   Component → hook → api function → client, one direction, rule book §3.
   The wire is the standard envelope (`backend/core/responses.py`), read in the
   one place that knows about it: `unwrap`. Paths are bare — `client.baseURL`
   already carries `/api/v1`. */
import { useQuery } from "@tanstack/react-query";

import { planNamed } from "@/shared/lib/plans";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type { PlanTier, ProfilePatch, Settings } from "@/shared/types";

export const settingsKeys = {
  all: ["settings"] as const,
};

export async function getSettings(): Promise<Settings> {
  return unwrap(await client.get<Envelope<Settings>>("/settings"));
}

/** `PATCH /settings` — only the keys that changed; `null` clears one. */
export async function patchSettings(patch: ProfilePatch): Promise<Settings> {
  return unwrap(await client.patch<Envelope<Settings>>("/settings", patch));
}

/* `meta.silent` opts out of the global error toast (`shared/api/queryClient.ts`): this
   is the boot query, and the boot gate already renders the failure full-screen
   with its own retry. A toast on top of that card would say the same thing
   twice. Every other query wants the toast — that is the default. */
export function useSettings() {
  return useQuery({
    queryKey: settingsKeys.all,
    queryFn: getSettings,
    staleTime: 60_000,
    meta: { silent: true },
  });
}

/* The org's plan. The server owns it; until the boot query answers this reads
   the entry plan, which under-promises rather than flashing a plan the org is
   not on. */
export function usePlan(): PlanTier {
  const { data } = useSettings();
  return planNamed(data?.plan.id ?? "free");
}
